import { Controller } from "@hotwired/stimulus"
import {
  launchEmbeddedSignup,
  SignupCancelled,
  featureTypeFor,
} from "../../../frontend/src/shared/metaEmbeddedSignup"

// Universal Desk's one setup step: open Meta's dialog, hand the server what it
// returns, and land the customer in the inbox their number now feeds.
//
// The flow editor drives the same dialog from React and calls the same util, so
// Meta's protocol is described once. Meta renames its finishing events over
// time, and a second copy of that logic would be a desk stuck on a spinner
// while the editor kept working.
export default class extends Controller {
  static targets = ["button", "error"]
  static values = {
    appId: String,
    configId: String,
    solutionId: String,
    graphVersion: String,
    url: String,
    fallbackUrl: String,
    brandId: String,
  }

  async launch(event) {
    // Which of the two offers on the screen was pressed. Meta onboards the
    // number one of two ways, and only one of them leaves WhatsApp working on
    // the owner's phone, so the answer has to reach both Meta's dialog and the
    // connection the server stores.
    const coexistence = event.params.coexistence === true

    this.clearError()
    this.setBusy(true)

    try {
      const grant = await launchEmbeddedSignup({
        appId: this.appIdValue,
        configId: this.configIdValue,
        solutionId: this.solutionIdValue,
        graphVersion: this.graphVersionValue,
        featureType: featureTypeFor(coexistence),
      })

      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          code: grant.code,
          waba_id: grant.wabaId,
          phone_number_id: grant.phoneNumberId,
          coexistence,
          brand_id: this.brandIdValue,
        }),
      })

      // A refusal is answered as JSON with a reason the customer can act on. A
      // response that is not JSON at all, a lapsed session redirected to login
      // being the usual one, has no reason in it to read.
      const body = await response.json().catch(() => ({}))

      if (!response.ok) {
        this.showError(body.error || "That number could not be connected. Please try again.")
        return
      }

      window.location.assign(body.redirect_url || this.fallbackUrlValue)
    } catch (error) {
      // Backing out of Meta's dialog is a choice, not a failure.
      if (!(error instanceof SignupCancelled)) {
        this.showError(error.message)
      }
    } finally {
      this.setBusy(false)
    }
  }

  // Both offers go quiet together. They open the same dialog on different terms,
  // so a second one started over the first would race it for the same number.
  setBusy(busy) {
    this.buttonTargets.forEach((button) => {
      button.disabled = busy
    })
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.add("pu-alert", "pu-alert-danger")
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.remove("pu-alert", "pu-alert-danger")
  }
}
